import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { shallow } from 'zustand/shallow';

import {
  changeUserSpaceMembership,
  SpaceMembershipOperation,
} from '../../api/Rest/space';
import { currencyAmonuts } from '../../components/Molecules';
import {
  HandwritingGameMode,
  TextOrderingGameMode,
} from '../../components/Organisms';
import {
  HeadphonesSuggestedInfoPopup,
  LeaveGamePopup,
} from '../../components/Popups';
import { QuestionAnswerLanguageSelectPopup } from '../../components/Popups/QuestionAnswerLanguageSelectPopup';
import GameEndTemplate, {
  AnimatedNumberProps,
  GameEndButtonText,
} from '../../components/Templates/Game/GameEndTemplate/GameEndTemplate';
import GameplayTemplate from '../../components/Templates/Game/GameplayTemplate/GameplayTemplate';
import GameStartTemplate from '../../components/Templates/Game/GameStartTemplate/GameStartTemplate';
import { StaminaType } from '../../constants/currencies';
import { getGameTypeDetails } from '../../constants/Games/gameType';
import { IconType } from '../../constants/icons';
import { getLanguageDetail } from '../../constants/languageDetails';
import { getCandidateAnswerSlot } from '../../constants/MemoryGames/spellingChar';
import { SVGIconType } from '../../constants/svgIcon';
import { AppToastInfoOption } from '../../constants/toast';
import { usePopup } from '../../context';
import useAbortControllerSignal from '../../hooks/useAbortControllerSignal';
import useAudioPlayer from '../../hooks/useAudioPlayer';
import useGameCurrency from '../../hooks/useGameCurrency';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import { TranslationLocale } from '../../i18n';
import { Balance, BalanceType, Locale, TtsLocale } from '../../models/Base';
import {
  EvaluationType,
  GamePlayState,
  GameScreen,
} from '../../models/GameControllerStore';
import {
  FingerWritingAnswer,
  GameType,
  TextOrderingAnswer,
  TextOrderingUnitType,
} from '../../models/GameStore';
import {
  ChallengeRecord,
  InAppEvaluation,
  TextEvaluationWord,
} from '../../models/RecordStore';
import { GameListsType } from '../../models/SessionStore';
import { Profile } from '../../models/UserStore';
import { useFileStore } from '../../stores/FileStore';
import {
  GAME_MODE_SETUP_FN,
  GameMode,
  InGameSoundEffects,
  useGameController,
  useGameControllerStore,
} from '../../stores/GameControllerStore';
import { useGameStore } from '../../stores/GameStore';
import { useRecordStore } from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useTimer, useTimerState } from '../../stores/TimeStore';
import { useUserStore } from '../../stores/UserStore';
import { getTimeString } from '../../utils/date';
import { getUiLocale } from '../../utils/i18n';
import { customLog } from '../../utils/logger';
import { selectRandomWords } from '../../utils/string';

import './Game.css';
import gameStartStyles from '../../components/Templates/Game/GameStartTemplate/GameStartTemplate.module.css';
const { fetchSpace } = useSpaceStore.getState();
const Game = () => {
  const navigate = useNavigate();

  useRedirectEntry();

  const {
    classroomId = '',
    gameSetId = '',
    gameId = '',
    challengeIndex = '',
  } = useParams();
  const query = new URLSearchParams(useLocation().search);
  const mode = query.get('mode');
  const { t } = useTranslation();
  const signal = useAbortControllerSignal();
  const audioPlayer = useAudioPlayer();
  const timer = useTimer('game');
  const { showPopup, hidePopup } = usePopup();

  useEffect(() => {
    return () => {
      useGameControllerStore.getState().cleanup();
      useRecordStore.getState().syncPlayingRecord().catch(console.error); // FIXME: no more playing records because await when init
    };
  }, []);

  useEffect(() => {
    useUserStore.getState().profiles.list('gamePlay').addListener();
    useGameStore.getState().gameCollections.list('gamePlay').addListener();
    useSpaceStore.getState().spaces.list('gamePlay').addListener();
    useSpaceStore.getState().albums.list('gamePlay').addListener();
    useGameStore.getState().games.list('gamePlay').addListener();
    useRecordStore.getState().gameRecords.list('gamePlay').addListener();
    useRecordStore.getState().challengeRecords.list('gamePlay').addListener();
    useGameStore.getState().gameStatistics.list('gamePlay').addListener();
    return () => {
      useUserStore.getState().profiles.list('gamePlay').removeListener();
      useSpaceStore.getState().spaces.list('gamePlay').removeListener();
      useSpaceStore.getState().albums.list('gamePlay').removeListener();
      useGameStore.getState().gameCollections.list('gamePlay').removeListener();
      useGameStore.getState().games.list('gamePlay').removeListener();
      useRecordStore.getState().gameRecords.list('gamePlay').removeListener();
      useRecordStore
        .getState()
        .challengeRecords.list('gamePlay')
        .removeListener();
      useGameStore.getState().gameStatistics.list('gamePlay').removeListener();
    };
  }, []);

  useEffect(() => {
    // TODO: stop time manangement (haltModal)
  }, []);

  useEffect(() => {
    const m = gameId ? GameMode.Default : GameMode.SimpleChallenge;
    const {
      extractDisplayData,
      prepareIntermediate,
      fetchData,
      refineResult,
      initializeRecord,
      finalizeRecord,
    } = GAME_MODE_SETUP_FN[m];
    const userId = useSessionStore.getState().activeUserId as string;
    const locale = useUserStore.getState().getAccount(userId)?.settings
      .interfaceLocale;
    useGameControllerStore
      .getState()
      .setup(
        {
          t,
          mode: m,
          locale,
          userId,
          signal,
        },
        {
          classroomId,
          gameSetId,
          gameId,
          challengeIndex: ['comprehensive', 'super'].includes(challengeIndex)
            ? challengeIndex
            : parseInt(challengeIndex),
          isAssistedGame: mode === 'tutorial',
          isRevisionGame: mode === 'revision',
        },
        extractDisplayData,
        prepareIntermediate,
        fetchData,
        refineResult,
        initializeRecord,
        finalizeRecord,
        audioPlayer,
        timer,
      )
      .then(() => {
        const { error } = useGameControllerStore.getState();
        if (error) {
          console.warn(error.message);
        }
      })
      .catch(console.error);
  }, [
    classroomId,
    gameSetId,
    gameId,
    challengeIndex,
    mode,
    t,
    signal,
    audioPlayer,
    timer,
  ]);

  const gameController = useGameController();
  const [isShowingPopup, setIsShowingPopup] = useState(false);

  const onClickCancelExitGame = useCallback(() => {
    hidePopup();
    setIsShowingPopup(false);
    gameController?.setGamePlayState(GamePlayState.Resume).catch(console.error);

    // window.history.pushState(null, '', window.location.pathname);
    // setTimeout(() => {
    //   window.history.pushState(null, '', window.location.pathname);
    // }, 0);
  }, [gameController, hidePopup]);

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      if (
        useGameControllerStore.getState().gamePlayState ===
        GamePlayState.Running
      ) {
        gameController
          ?.setGamePlayState(GamePlayState.Pause)
          .catch(console.error);
        setIsShowingPopup(true);
        showPopup(
          <LeaveGamePopup
            onClickCancel={onClickCancelExitGame}
            onClickLeave={() => {
              hidePopup();
              setIsShowingPopup(false);
              useGameControllerStore.getState().onLeaveGame(navigate);
            }}
          />,
          { backdropCallback: () => onClickCancelExitGame() },
        );
      } else {
        useGameControllerStore.getState().onLeaveGame(navigate);
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // if (
      //   useGameControllerStore.getState().gamePlayState ===
      //   GamePlayState.Running
      // ) {
      gameController
        ?.setGamePlayState(GamePlayState.Pause)
        .catch(console.error);
      e.preventDefault();
      e.returnValue = '';
      // return '';
      // }
      // return undefined;
    };
    const handleFocus = () => {
      if (
        useGameControllerStore.getState().gamePlayState ===
          GamePlayState.Pause &&
        isShowingPopup
      ) {
        gameController
          ?.setGamePlayState(GamePlayState.Resume)
          .catch(console.error);
      }
    };

    // window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('focus', handleFocus);
    };
  }, [
    gameController,
    hidePopup,
    isShowingPopup,
    navigate,
    onClickCancelExitGame,
    showPopup,
  ]);

  const error = useGameControllerStore((state) => state.error);
  const gameScreen = useGameControllerStore((state) => state.gameScreen);

  if (error) {
    return <div className="game-screen">{error.message}</div>;
  }

  return (
    <div className="game-screen">
      {gameScreen === GameScreen.GameStart && <GameStart />}
      {gameScreen === GameScreen.GamePlay && <GamePlay />}
      {gameScreen === GameScreen.GameEnd && <GameEnd />}
    </div>
  );
};

const GameStart = () => {
  const activeUserId = useSessionStore((store) => store.activeUserId);
  const account = useUserStore((state) => state.getAccount(activeUserId ?? ''));
  const profile = useUserStore((state) => state.getProfile(activeUserId ?? ''));
  const { classroomId = '', gameSetId = '', gameId = '' } = useParams();
  const [initAnswer, setInitAnswer] = useState<TextOrderingAnswer | undefined>(
    undefined,
  );
  // MARK: Get Space
  const spaceListFieldName = `${classroomId}`;
  useSpaceStoreListField('spaces', spaceListFieldName);

  useEffect(() => {
    if (classroomId) {
      fetchSpace(spaceListFieldName, classroomId).catch(console.error);
    }
  }, [classroomId, spaceListFieldName]);

  const { classroom, classroomMembership } = useSpaceStore((store) => {
    const space = store.getSpace(classroomId ?? '');
    const membership = store.getActiveUserMembership(classroomId ?? '');
    return {
      classroom: space,
      classroomMembership: membership,
    };
  }, shallow);

  const visitClassroom = useCallback(() => {
    changeUserSpaceMembership(
      { classroomId: classroomId ?? '' },
      {
        operation: SpaceMembershipOperation.Visit,
      },
    ).catch(console.error);
  }, [classroomId]);

  useEffect(() => {
    if (classroom && classroomMembership === null) {
      visitClassroom();
    }
  }, [classroom, classroomMembership, visitClassroom]);

  const navigate = useNavigate();
  const {
    // clubIcon,
    // clubName,
    theme,
    gameTitle,
    gameIcon,
    typeName,
    latestScore,
    latestTime,
    playCount,
    avgScore,
    reward,
    isChallenge,
    isAssistedGame,
    gameFee,
    gameBGIndex,
    gameCount,
    opponentProfile,
    withAIBadge,
  } = useGameControllerStore((store) => {
    return {
      classroomId: store.params.classroomId,
      clubIcon: store.displayData?.context.icon,
      clubName: store.displayData?.context.title,
      theme: store.displayData?.theme,
      gameTitle: store.displayData?.body.title ?? 'Game',
      gameIcon:
        (store.displayData?.body.icon as IconType) ??
        IconType.GameTypeSpellingBig,
      // gameModeName: store.displayData?.body.modeName,
      typeName: store.displayData?.body.gameIconTag,
      latestScore: store.displayData?.latestRecord?.score,
      latestTime: store.displayData?.latestRecord?.durationInMs,
      playCount:
        store.params.challengeIndex === 'comprehensive'
          ? undefined
          : store.displayData?.statistics?.playCount ?? 0,
      avgScore:
        store.params.challengeIndex === 'comprehensive'
          ? undefined
          : store.displayData?.statistics?.averageScore ?? 0,
      reward: store.displayData?.maximumReward,
      isChallenge: store.displayData?.isChallenge ?? true,
      isAssistedGame: store.params.isAssistedGame,
      gameFee: store.displayData?.fee.token,
      gameBGIndex: store.displayData?.gameBGIndex,
      gameCount: store.gameSequence?.length ?? 0,
      opponentProfile: store.displayData?.dummies?.profiles[0],
      withAIBadge: store.displayData?.dummies?.opponents[0]?.isPlayedByBot,
    };
  });

  const { gameType, answer } = useGameControllerStore((store) => {
    const _gameType = store.gameSequence?.[store.index ?? 0]?.gameType;
    if (_gameType === GameType.TextOrdering) {
      const a = cloneDeep(
        store.gameSequence?.[store.index ?? 0]?.gameData
          .answer as TextOrderingAnswer,
      );

      return {
        gameType: _gameType,
        answer: a,
      };
    }
    if (_gameType === GameType.FingerWriting) {
      const a = cloneDeep(
        store.gameSequence?.[store.index ?? 0]?.gameData
          .answer as FingerWritingAnswer,
      );

      return {
        gameType: _gameType,
        answer: a,
      };
    }

    return {
      gameType: _gameType,
      answer: undefined,
    };
  });

  const { t } = useTranslation();
  const { showPopup, hidePopup } = usePopup();

  const allowance = useUserStore((store) => store.getAllowance());

  const { gameStamina, token, checkBalance } = useGameCurrency(
    account,
    profile,
    allowance,
  );

  // FIXME: maybe update the account balance in frontend to avoid ui values reverting back if-
  // user quit game and quickly come back to game start before getCurrentUser finish updating
  const [displayedToken, setDisplayedToken] = useState(token ?? 0);
  const [displayedStamina, setDisplayedStamina] = useState(gameStamina ?? 0);

  const [paidToken, setPaidToken] = useState(0);
  const [paidStamina, setPaidStamina] = useState(0);

  const [isShowAssetBar, setIsShowAssetBar] = useState(false);

  const assetCurrencies = useMemo(
    () =>
      currencyAmonuts(
        { gameStamina: displayedStamina, token: displayedToken },
        [StaminaType.GameStamina, BalanceType.Token],
      ),
    [displayedStamina, displayedToken],
  );

  const staminaAnimatedNumberProps: AnimatedNumberProps = useMemo(() => {
    return {
      value: paidStamina,
      style: gameStartStyles.payStaminaPos,
      onAnimationEnd() {
        setPaidStamina(-1);
      },
      addEXP() {
        setDisplayedStamina(displayedStamina - 1);
      },
      plusOrMinus: false,
    };
  }, [displayedStamina, paidStamina]);

  const tokenAnimatedNumberProps: AnimatedNumberProps = useMemo(() => {
    return {
      value: paidToken,
      style: gameStartStyles.payTokenPos,
      onAnimationEnd() {
        setPaidToken(-1);
      },
      addEXP() {
        setDisplayedToken(displayedToken - 1);
      },
      plusOrMinus: false,
    };
  }, [displayedToken, paidToken]);

  const onClickStart = useCallback(() => {
    checkBalance(StaminaType.GameStamina, gameFee ?? 0, () => {
      useGameControllerStore.getState().onGameStart().catch(console.error);
      if (gameFee && gameFee > 0 && displayedStamina >= gameFee) {
        setIsShowAssetBar(true);
        setPaidStamina(gameFee);
      } else if (gameFee && gameFee > 0 && displayedToken >= gameFee) {
        setIsShowAssetBar(true);
        setPaidToken(gameFee);
      }
    });
  }, [checkBalance, displayedStamina, displayedToken, gameFee]);

  const onClickHeadphoneSuggested = useCallback(() => {
    showPopup(<HeadphonesSuggestedInfoPopup onPrimary={hidePopup} />);
  }, [hidePopup, showPopup]);

  const onClickExit = useCallback(() => {
    useGameControllerStore.getState().onLeaveGame(navigate);
  }, [navigate]);

  const opponent: Profile[] =
    !isChallenge && !isAssistedGame && opponentProfile ? [opponentProfile] : [];

  const onClickShare = useCallback(() => {
    const url = new URL(window.location.href);
    const cleanUrl = `${url.origin}${url.pathname}/share${
      isAssistedGame ? '?mode=tutorial' : ''
    }`;

    if (navigator.share) {
      navigator.share({ url: decodeURIComponent(cleanUrl) }).catch((err) => {
        console.error('Failed to share the URL: ', err);
      });
    } else {
      navigator.clipboard
        .writeText(decodeURIComponent(cleanUrl))
        .then(() => {
          toast(t('Link is copied!'), {
            ...AppToastInfoOption,
            id: 'clipboard',
          });
        })
        .catch((err) => {
          console.error('Failed to copy the URL: ', err);
        });
    }
  }, [isAssistedGame, t]);

  const onClickMemoryHelper = useCallback(() => {
    if (gameId) {
      navigate(
        `/club/${classroomId}/journey/${gameSetId}/game/${gameId}/helper`,
      );
    }
  }, [classroomId, gameId, gameSetId, navigate]);

  const questionPanel = useMemo(() => {
    if (gameType === GameType.TextOrdering) {
      let modifiedAnswer = answer;
      if (!initAnswer) {
        const originalAnswer = cloneDeep(answer);
        const splitWords: string[] = [];
        const wordWeights: number[] = [];
        let wordCount = 0;
        originalAnswer.words.forEach((word, i) => {
          word.units.forEach((unit, j) => {
            splitWords.push(`${i}_${j}`);
            wordWeights.push(
              unit.type === TextOrderingUnitType.Candidate
                ? unit.key.length
                : 0,
            );
            if (unit.type === TextOrderingUnitType.Candidate) {
              wordCount += 1;
            }
          });
        });
        if (wordCount > 0) {
          const indexes = selectRandomWords(
            splitWords,
            wordWeights,
            getCandidateAnswerSlot(wordCount, answer.useColor),
          );

          originalAnswer.words.forEach((word, i) => {
            word.units.forEach((unit, j) => {
              if (
                indexes.find(
                  (selectedIndex) => splitWords[selectedIndex] === `${i}_${j}`,
                ) !== undefined
              ) {
                unit.type = TextOrderingUnitType.Blank;
              } else {
                unit.type = TextOrderingUnitType.Hint;
              }
            });
          });

          modifiedAnswer = originalAnswer;
          setInitAnswer(originalAnswer);
        }
      } else {
        modifiedAnswer = initAnswer;
      }

      let mode: TextOrderingGameMode;
      if (modifiedAnswer.answerFirstLetter) {
        mode = TextOrderingGameMode.QuickMemory;
      } else if (modifiedAnswer.useColor) {
        mode = TextOrderingGameMode.Syllable;
      } else {
        mode = TextOrderingGameMode.Spelling;
      }

      return {
        textAnswer: modifiedAnswer,
        blankInfos: {},
        mode: mode,
      };
    }

    if (gameType === GameType.FingerWriting) {
      return {
        writingAnswer: answer,
        blankInfos: {},
        mode: HandwritingGameMode.QuickHandwriting,
      };
    }

    return undefined;
  }, [answer, gameType, initAnswer]);

  return profile ? (
    <GameStartTemplate
      profile={profile}
      currencies={assetCurrencies}
      isAssistedGame={isAssistedGame}
      gameFee={gameFee}
      gameBGIndex={gameBGIndex}
      gameStartInfoBoardProps={{
        // clubIcon,
        // clubName,
        theme,
        gameTitle,
        gameIcon,
        challengeGameTotal: isChallenge ? gameCount : undefined,
        gameModeName: isChallenge
          ? undefined
          : isAssistedGame
          ? 'Interactive Learning'
          : 'Memory Practice',
        typeName: typeName
          ? getGameTypeDetails(typeName).displayName
          : undefined,
        latestScore,
        latestTime,
        playCount,
        avgScore,
        onClickShare,
      }}
      gameStartVSBoardProps={{
        profile: profile,
        opponentRecords: opponent,
        withAIBadge: withAIBadge,
        reward: !isAssistedGame && reward ? new Balance(reward) : undefined,
      }}
      memoryHelperProps={{
        questionPanel: questionPanel,
        isUseTranslate: false,
        originalLanguage: '',
        translatedLanguage: '',
        originalAudioTts: undefined,
        translatedAudioTts: undefined,
        translationResult: undefined,
        isGameStart: true,
        onSwitchTranslate: () => {},
        onClickLanguage: () => {},
        onClickMemoryHelper: onClickMemoryHelper,
      }}
      onClickStart={onClickStart}
      onClickHeadphonesSuggested={onClickHeadphoneSuggested}
      isLoading={useGameControllerStore.getState().isLoading}
      onClickExit={onClickExit}
      onClickShare={onClickShare}
      onClickMemoryHelper={onClickMemoryHelper}
      staminaAnimatedNumberProps={staminaAnimatedNumberProps}
      tokenAnimatedNumberProps={tokenAnimatedNumberProps}
      isShowAssetBar={isShowAssetBar}
    />
  ) : (
    <></>
  );
};

const GamePlay = () => {
  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );
  const [previousIndex, setPreviousIndex] = useState(0);
  const [previousScore, setPreviousScore] = useState(0);
  const [scoreDeducted, setscoreDeducted] = useState(0);
  const { gameType, answer, evaluationForImprovement } = useGameControllerStore(
    (store) => {
      const _gameType = store.gameSequence?.[store.index ?? 0]?.gameType;
      if (_gameType === GameType.TextOrdering) {
        const a = cloneDeep(
          store.gameSequence?.[store.index ?? 0]?.gameData
            .answer as TextOrderingAnswer,
        );
        const evaluation = store.evaluationForImprovement;
        evaluation?.word?.forEach((word, i) => {
          word.units.forEach((unit, j) => {
            if (!unit.isBlank || (unit.score ?? 0) > 95) {
              if (a.words[i]?.units[j]?.type) {
                a.words[i]!.units[j]!.type = TextOrderingUnitType.Hint;
              }
            } else {
              if (a.words[i]?.units[j]?.type) {
                a.words[i]!.units[j]!.type = unit.isBlank
                  ? TextOrderingUnitType.Blank
                  : TextOrderingUnitType.Hint;
              }
            }
          });
        });

        return {
          gameType: _gameType,
          answer: a,
          evaluationForImprovement: evaluation,
        };
      }
      if (_gameType === GameType.FingerWriting) {
        const a = cloneDeep(
          store.gameSequence?.[store.index ?? 0]?.gameData
            .answer as FingerWritingAnswer,
        );
        const evaluation = store.evaluationForImprovement;
        evaluation?.word?.forEach((word, i) => {
          word.units.forEach((unit, j) => {
            if (!unit.isBlank || (unit.score ?? 0) > 95) {
              if (a.words[i]?.characters[j]?.strokeCount) {
                a.words[i]!.characters[j]!.strokeCount = undefined;
              }
            }
          });
        });

        return {
          gameType: _gameType,
          answer: a,
          evaluationForImprovement: evaluation,
        };
      }

      return {
        gameType: _gameType,
        answer: undefined,
        evaluationForImprovement: undefined,
      };
    },
  );

  const {
    index,
    gameCount,
    isChallenge,
    isAssistedGame,
    isRevisionGame,
    gameBGIndex,
    gamePlayState,
    challengeTitle,
    challengeDurationInMs,
    instruction,
    strokeDatas,
    userScore,
    // typeName,
    opponentProfile,
    opponentScore,
    withAIBadge,
    questionTtsFileId,
  } = useGameControllerStore((store) => {
    let cTitle;
    let cDurationInMs = 0;
    if (store.displayData?.isChallenge) {
      cTitle = 'Mini Challenge';
      if (store.params.challengeIndex === 'comprehensive') {
        cTitle = 'Challenge All';
      } else if (store.params.challengeIndex === 'super') {
        cTitle = 'Super Challenge';
      }
      cDurationInMs =
        store.record?.challengeRecord?.games.reduce(
          (acc, cur) => acc + (cur.durationInMs || 0),
          0,
        ) ?? 0;
    }

    return {
      index: store.index ?? 0,
      gameCount: store.gameSequence?.length ?? 0,
      isChallenge: store.displayData?.isChallenge,
      isAssistedGame: store.params.isAssistedGame,
      isRevisionGame: store.params.isRevisionGame,
      gameBGIndex: store.displayData?.gameBGIndex,
      gamePlayState: store.gamePlayState,
      challengeTitle: cTitle,
      challengeDurationInMs: cDurationInMs,
      instruction:
        store.gameSequence?.[store.index ?? 0]?.gameData.instruction.text,
      strokeDatas: store.strokeDatas,
      userScore: store.score,
      // typeName: store.displayData?.body.gameIconTag ?? 'spelling',
      opponentProfile: store.displayData?.dummies?.profiles[0],
      opponentScore: store.displayData?.dummies?.opponents[0]?.score,
      questionTtsFileId:
        store.gameSequence?.[store.index ?? 0]?.gameData.instruction.sound
          ?.fileId,
      withAIBadge: store.displayData?.dummies?.opponents[0]?.isPlayedByBot,
    };
  });

  const gameController = useGameController();

  const timer = useTimerState('game', Math.floor(1000 / 24));

  useEffect(() => {
    if (gamePlayState === GamePlayState.End) {
      useGameControllerStore
        .getState()
        .onGameStageEnd(timer.overallTimeElapsed)
        .catch(console.error);
    }
  }, [gamePlayState, timer.overallTimeElapsed]);

  const profiles: Profile[] = [];
  const scores = useMemo<number[]>(() => {
    if (!userProfile) return [];

    const result = [userScore];
    if (
      !isChallenge &&
      !isAssistedGame &&
      evaluationForImprovement === undefined
    ) {
      result.push(opponentScore ?? 50);
    }
    return result;
  }, [
    userProfile,
    userScore,
    isChallenge,
    isAssistedGame,
    evaluationForImprovement,
    opponentScore,
  ]);

  if (userProfile) {
    profiles.push(userProfile);
    // scores.push(userScore);
    if (
      !isChallenge &&
      !isAssistedGame &&
      evaluationForImprovement === undefined
    ) {
      profiles.push(opponentProfile ?? userProfile);
      // scores.push(opponentScore ?? 50);
    }
  }

  useEffect(() => {
    const currentScore = scores[0] ?? 0;
    const scoreChange = currentScore - previousScore;
    setPreviousScore(currentScore);
    if (scoreChange !== 0 && previousScore !== 0) {
      setscoreDeducted(0);

      if (index === previousIndex) {
        setscoreDeducted(scoreChange);
      }
      setPreviousIndex(index);

      setTimeout(() => {
        setscoreDeducted(0);
      }, 800);
    }
  }, [index, previousIndex, previousScore, scores]);

  return (
    <GameplayTemplate
      key={index}
      gameBGIndex={gameBGIndex}
      questionTtsFileId={questionTtsFileId}
      gameController={gameController}
      gameHudProps={{
        profiles,
        gameType: gameType,
        scores,
        elapsedTime: getTimeString(
          challengeDurationInMs + timer.overallTimeElapsed,
          'MM:ss.SS',
        ),
        instruction: instruction,
        deductingScore: scoreDeducted,
        challengeTitle,
        challengeGameIndex: index,
        challengeGameCount: gameCount,
        evaluation: evaluationForImprovement,
        withAIBadge: withAIBadge,
      }}
      memoryGameMainGroupProps={
        gameType === GameType.TextOrdering
          ? {
              gamePlayState,
              answer,
              gameController,
              questionTtsFileId,
              isAssistedGame,
              isRevisionGame,
              evaluationType: evaluationForImprovement
                ? EvaluationType.Improvement
                : EvaluationType.InApp,
            }
          : undefined
      }
      handwritingGameMainGroupProps={
        gameType === GameType.FingerWriting
          ? {
              gamePlayState,
              answer: answer,
              gameController,
              strokeData: strokeDatas,
              questionTtsFileId,
              isAssistedGame,
              isRevisionGame,
              evaluationType: evaluationForImprovement
                ? EvaluationType.Improvement
                : EvaluationType.InApp,
            }
          : undefined
      }
      isLoading={useGameControllerStore.getState().isLoading}
    />
  );
};

type State = {
  isUseTranslate: boolean;
  tempLanguage: TtsLocale | undefined;
  originalLanguage: TtsLocale | undefined;
  translatedLanguage: TtsLocale | undefined;
  originalAudioTts: string | null | undefined;
  translatedAudioTts: string | null | undefined;
  translationResult: string | undefined;
  buttonText: GameEndButtonText | undefined;
  isLoadingTotal: number;
};

const { getTtsAudios, getTranslationText } = useFileStore.getState();

const GameEnd = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showPopup, hidePopup } = usePopup();
  const signal = useAbortControllerSignal();

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );
  const gameListsData = useSessionStore((store) => store.gameListsData);

  const [state, setState] = useState<State>({
    isUseTranslate: false,
    tempLanguage: undefined,
    originalLanguage: undefined,
    translatedLanguage: undefined,
    originalAudioTts: undefined,
    translatedAudioTts: undefined,
    translationResult: undefined,
    buttonText: undefined,
    isLoadingTotal: 0,
  });

  const {
    isChallenge,
    challengeIndex,
    userScore,
    userDurationInMs,
    opponentProfile,
    opponentScore,
    opponentDurationInMs,
    evaluationForImprovement,
    gameType,
    game,
    inAppEvaluations,
    rewardCoin,
    rewardEXP,
    gameBGIndex,
    isAssistedGame,
    audioPlayer,
    withAIBadge,
  } = useGameControllerStore((store) => {
    const _isAssistedGame = store.params.isAssistedGame;
    const wasImprovementGame =
      store.improvementPlayCount + store.fullPlayCount > 0;

    let score;
    let durationInMs;
    if (store.displayData?.isChallenge) {
      if (store.record?.challengeRecord) {
        const challengeRecord = new ChallengeRecord(
          store.record.challengeRecord,
        );
        customLog(challengeRecord, userProfile);
        score = challengeRecord.score;
        durationInMs = challengeRecord.durationInMs;
      }
    } else {
      score = store.record?.gameRecord?.score;
      durationInMs = store.record?.gameRecord?.durationInMs;
    }

    const _rewardCoin =
      (store.record?.gameRecord?.bonusReward?.breadSlice ?? 0) +
      (store.record?.gameRecord?.reward?.breadSlice ?? 0);

    const _rewardEXP =
      (store.record?.gameRecord?.bonusReward?.exp ?? 0) +
      (store.record?.gameRecord?.reward?.exp ?? 0);

    const _gameType =
      store.gameSequence?.[store.index ?? 0]?.gameType ?? GameType.TextOrdering;

    const _game = store.gameSequence?.[store.index ?? 0]?.gameData;

    return {
      isChallenge: store.displayData?.isChallenge ?? true,
      challengeIndex: store.params.challengeIndex,
      userScore: score,
      userDurationInMs: durationInMs,
      opponentProfile:
        _isAssistedGame || wasImprovementGame
          ? undefined
          : store.displayData?.dummies?.profiles[0],
      opponentScore:
        _isAssistedGame || wasImprovementGame
          ? undefined
          : store.displayData?.dummies?.opponents[0]?.score,
      opponentDurationInMs:
        _isAssistedGame || wasImprovementGame
          ? undefined
          : store.displayData?.dummies?.opponents[0]?.durationInMs,
      evaluationForImprovement: store.evaluationForImprovement,
      gameType: _gameType,
      game: _game,
      inAppEvaluations: store.inAppEvaluations,
      rewardCoin: _rewardCoin,
      rewardEXP: _rewardEXP,
      gameBGIndex: store.displayData?.gameBGIndex,
      isAssistedGame: _isAssistedGame,
      audioPlayer: store.audioPlayer,
      withAIBadge: store.displayData?.dummies?.opponents[0]?.isPlayedByBot,
    };
  });
  let buttonText = GameEndButtonText.NextStation;
  let evaluationKey = EvaluationType.InApp;
  if (challengeIndex === 'comprehensive') {
    buttonText = GameEndButtonText.Exit;
  } else if (challengeIndex === 'super') {
    buttonText = GameEndButtonText.Goal;
  } else if (evaluationForImprovement) {
    buttonText = GameEndButtonText.PlayImprovementGame;
    evaluationKey = evaluationForImprovement.type;
  } else if (evaluationForImprovement === null) {
    buttonText = GameEndButtonText.PlayFullGame;
    evaluationKey = EvaluationType.Improvement;
  } else if (gameListsData !== null) {
    if (gameListsData.gameLinkList.length !== 0) {
      buttonText = GameEndButtonText.NextPractice;
    } else {
      buttonText = GameEndButtonText.Exit;
    }
  }

  useEffect(() => {
    if (!state.buttonText) {
      setState((s) => ({ ...s, buttonText: buttonText }));
    }
  }, [buttonText, state.buttonText]);

  useEffect(() => {
    audioPlayer.playAudio(InGameSoundEffects.gameEndSound);
  }, [audioPlayer]);

  const lastEvaluation = inAppEvaluations[inAppEvaluations.length - 1];

  const gameEvaluation: InAppEvaluation<TextEvaluationWord> | undefined =
    lastEvaluation
      ? {
          gameType: gameType,
          inApp: lastEvaluation.word,
          improvement: lastEvaluation.word,
        }
      : undefined;

  // MARK: Exit
  const { classroomId = '', gameSetId = '', gameId = '' } = useParams();
  const handleExit = () => {
    useGameControllerStore.getState().onLeaveGame(navigate);
  };

  useEffect(() => {
    if (game) {
      const locale = game.locale as TtsLocale;
      setState((s) => ({ ...s, originalLanguage: locale }));
    }
  }, [game]);

  const onGetTtsAudio = useCallback(
    async (text: string, locale: TtsLocale, isTranslateAudio: boolean) => {
      if (text) {
        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal + 1 }));
        const audioWithUrl = await getTtsAudios([text], locale, signal);
        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal - 1 }));
        if (audioWithUrl) {
          const url = audioWithUrl[text]?.url;
          if (url) {
            if (isTranslateAudio) {
              setState((s) => ({ ...s, translatedAudioTts: url }));
            } else {
              setState((s) => ({ ...s, originalAudioTts: url }));
            }
          }
        }
      }
    },
    [signal],
  );

  useEffect(() => {
    const lastInAppEvaluations = inAppEvaluations[inAppEvaluations.length - 1];
    if (lastInAppEvaluations && state.originalLanguage) {
      const words = lastInAppEvaluations.word.map((word) => word.word);

      const text = words?.join('\n');
      if (text) {
        onGetTtsAudio(text, state.originalLanguage, false).catch(console.error);
      }
    }
  }, [inAppEvaluations, onGetTtsAudio, state.originalLanguage]);

  useEffect(() => {
    setState((s) => ({
      ...s,
      translatedLanguage: getUiLocale(i18n.language as TranslationLocale),
    }));
  }, [i18n.language]);

  const onSwitchTranslate = useCallback(() => {
    setState((s) => ({ ...s, isUseTranslate: !s.isUseTranslate }));
  }, []);

  const onAutoTranslate = useCallback(async () => {
    const lastInAppEvaluations = inAppEvaluations[inAppEvaluations.length - 1];
    if (
      lastInAppEvaluations &&
      state.isUseTranslate &&
      state.translatedLanguage &&
      state.originalLanguage
    ) {
      const words = lastInAppEvaluations.word.map((word) => word.word);

      if (words) {
        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal + 1 }));
        const text = await getTranslationText(
          words,
          state.originalLanguage,
          state.translatedLanguage,
        );

        const translationText = text?.join(' ');

        if (translationText) {
          await onGetTtsAudio(translationText, state.translatedLanguage, true);
        }

        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal - 1 }));
        if (translationText) {
          setState((s) => ({
            ...s,
            translationResult: translationText,
          }));
        }
      }
    }
  }, [
    inAppEvaluations,
    onGetTtsAudio,
    state.isUseTranslate,
    state.originalLanguage,
    state.translatedLanguage,
  ]);

  useEffect(() => {
    if (
      state.isUseTranslate &&
      state.translatedLanguage &&
      state.originalLanguage
    ) {
      onAutoTranslate().catch(console.error);
    }
  }, [
    onAutoTranslate,
    state.isUseTranslate,
    state.originalLanguage,
    state.translatedLanguage,
  ]);

  const handleLanguagePopup = useCallback(
    (selectedLocale: TtsLocale) => {
      showPopup(
        <QuestionAnswerLanguageSelectPopup
          title={'Translation'}
          titleIcon={SVGIconType.Translation}
          languageList={undefined}
          selectedLanguage={selectedLocale}
          recentLanguage={undefined}
          onClickOK={() => {
            setState((s) => ({ ...s, translatedLanguage: s.tempLanguage }));
            hidePopup();
          }}
          onCancel={hidePopup}
          onSelectLanguage={(language: TtsLocale) => {
            setState((s) => ({ ...s, tempLanguage: language }));
            handleLanguagePopup(language); // Re-show popup with updated locale
          }}
        />,
      );
    },
    [hidePopup, showPopup],
  );

  const onClickLanguage = useCallback(() => {
    if (state.translatedLanguage) {
      setState((s) => ({ ...s, tempLanguage: s.translatedLanguage }));
      handleLanguagePopup(state.translatedLanguage);
    }
  }, [handleLanguagePopup, state.translatedLanguage]);

  const onClickAnalysis = useCallback(() => {
    if (gameId) {
      if (isAssistedGame) {
        navigate(
          `/club/${classroomId}/journey/${gameSetId}/game/${gameId}/analysis?mode=tutorial`,
          {
            replace: true,
            state: {
              isUseTranslate: state.isUseTranslate,
              translatedLanguage: state.translatedLanguage,
            },
          },
        );
      } else {
        navigate(
          `/club/${classroomId}/journey/${gameSetId}/game/${gameId}/analysis`,
          {
            replace: true,
            state: {
              isUseTranslate: state.isUseTranslate,
              translatedLanguage: state.translatedLanguage,
            },
          },
        );
      }
    }
  }, [
    classroomId,
    gameId,
    gameSetId,
    isAssistedGame,
    navigate,
    state.isUseTranslate,
    state.translatedLanguage,
  ]);

  const onClickShare = useCallback(() => {
    const url = new URL(window.location.href);
    const cleanUrl = `${url.origin}${url.pathname}/share${
      isAssistedGame ? '?mode=tutorial' : ''
    }`;

    if (navigator.share) {
      navigator.share({ url: decodeURIComponent(cleanUrl) }).catch((err) => {
        console.error('Failed to share the URL: ', err);
      });
    } else {
      navigator.clipboard
        .writeText(decodeURIComponent(cleanUrl))
        .then(() => {
          toast(t('Link is copied!'), {
            ...AppToastInfoOption,
            id: 'clipboard',
          });
        })
        .catch((err) => {
          console.error('Failed to copy the URL: ', err);
        });
    }
  }, [isAssistedGame, t]);

  const buttonProgress = useMemo(() => {
    if (gameListsData?.type === GameListsType.Standard) {
      const gamesLeft = gameListsData.gameLinkList.length;
      const totalGames = gameListsData.totalGames;
      if (gamesLeft > 0) {
        return {
          currentProgress: totalGames - gamesLeft,
          totalProgress: totalGames,
        };
      }
    }
    return undefined;
  }, [gameListsData]);

  return userProfile ? (
    <>
      <GameEndTemplate
        gameBGIndex={gameBGIndex}
        gameEndVSResultGroupProps={{
          currentUserProfile: userProfile,
          currentUserScore: userScore ?? 100,
          currentUserDuration: getTimeString(
            userDurationInMs ?? 10000,
            'M:ss.SS',
          ),
          opponentUserProfile: opponentProfile,
          opponentUserScore: opponentScore,
          opponentUserDuration: opponentDurationInMs
            ? getTimeString(opponentDurationInMs, 'M:ss.SS')
            : undefined,
          withAIBadge: withAIBadge,
        }}
        isChallenge={isChallenge}
        analysisEvaluation={gameEvaluation}
        evaluationKey={evaluationKey}
        answerLocale={state.originalLanguage ?? Locale.US}
        buttonText={state.buttonText}
        buttonProgress={buttonProgress}
        rewardEXP={rewardEXP}
        rewardBread={rewardCoin}
        isUseTranslate={state.isUseTranslate}
        originalLanguage={
          getLanguageDetail(state.originalLanguage)?.displayName ?? ''
        }
        translatedLanguage={
          getLanguageDetail(state.translatedLanguage)?.displayName ?? ''
        }
        originalAudioTts={state.originalAudioTts}
        translatedAudioTts={state.translatedAudioTts}
        translationResult={state.translationResult}
        isHandwriting={gameType === GameType.FingerWriting}
        isLoading={!!state.isLoadingTotal}
        isDisableRewardAnimation={inAppEvaluations.length > 1}
        onClickNext={() => {
          if (evaluationForImprovement === undefined) {
            // useGameControllerStore.getState().onLeaveGame(navigate);
            const fromPage = (location.state as { from: string })?.from;
            if (fromPage === 'analysisPage') {
              navigate(`/club/${classroomId}/journey/${gameSetId}`);
            } else {
              useGameControllerStore.getState().onNextGame(navigate);
            }
          } else {
            useGameControllerStore
              .getState()
              .onGameStart()
              .catch(console.error);
          }
        }}
        onClickExit={handleExit}
        onSwitchTranslate={onSwitchTranslate}
        onClickLanguage={onClickLanguage}
        onClickAnalysis={onClickAnalysis}
        onClickShare={onClickShare}
      />
    </>
  ) : (
    <></>
  );
};

export default Game;
